// ExpandingButtonsMenu.js

import React, { useState, useRef, useEffect } from "react";
import { Box, Text } from "grommet";

const ExpandingButtonsMenu = ({ buttons, direction }) => {
   const [activeButton, setActiveButton] = useState(null);
   const isMouseOverSubmenu = useRef(false);
   const timerRef = useRef(null);

   useEffect(() => {
       // Clear the timeout when the component unmounts
       return () => {
           clearTimeout(timerRef.current);
       };
   }, []);

   const handleMouseLeaveMenu = () => {
       timerRef.current = setTimeout(() => {
           if (!isMouseOverSubmenu.current) {
               setActiveButton(null);
           }
       }, 50);
   };

   const handleSubmenuEnter = () => {
       isMouseOverSubmenu.current = true;
       clearTimeout(timerRef.current);
   };

   const handleSubmenuLeave = () => {
       isMouseOverSubmenu.current = false;
       handleMouseLeaveMenu();
   };

   const handleButtonClick = (buttonId) => {
       setActiveButton((prevActive) => (prevActive === buttonId ? null : buttonId));
   };

   const formattedButtons = buttons.map((button) => ({
       ...button,
       content: button.letter ? (
           <Text size="large" weight="bold" color="white">
               {button.letter}
           </Text>
       ) : (
           button.icon
       ),
   }));

   return (
       <Box
           direction={direction}
           pad="small"
           align="start"
           justify="start"
           gap="xsmall"
           onMouseLeave={handleMouseLeaveMenu} // Close the menu when the mouse leaves the entire menu area
       >
           {formattedButtons
               .filter((button) => button.condition === undefined || button.condition)
               .map((button) => {
                   const isActive = activeButton === button.id;

                   return (
                       <Box
                           key={button.id}
                           style={{
                               zIndex: isActive ? 30 : 20,
                               width: "40px",
                               height: "40px",
                           }}
                       >
                           <Box
                               direction="row"
                               align="center"
                               justify={isActive ? "start" : "center"}
                               style={{
                                   width: isActive ? "260px" : "60px",
                                   transition: "width 0.3s ease-in-out",
                                   position: isActive ? "absolute" : "relative",
                                   zIndex: 10,
                                   backgroundColor: button.color,
                                   color: "white",
                                   borderRadius: "4px",
                                   cursor: "pointer",
                                   display: "flex",
                                   whiteSpace: "nowrap",
                                   overflow: "hidden",
                                   padding: "5px",
                                   boxShadow: isActive
                                       ? "0px 4px 8px rgba(0, 0, 0, 0.2)"
                                       : "none",
                               }}
                               onMouseEnter={() => {
                                   clearTimeout(timerRef.current)
                                   setActiveButton(button.id)
                               }}
                               onClick={() => {
                                   if (button.subMenu) {
                                       handleButtonClick(button.id);
                                   } else {
                                       button.onClick();
                                   }
                               }}
                           >
                               <Box
                                   border={{
                                       color: button.color,
                                       size: "small",
                                       style: "solid",
                                   }}
                                   round="small"
                                   align="center"
                                   justify="center"
                                   style={{ width: "35px", height: "35px" }}
                               >
                                   <Box
                                       border={{
                                           color: "white",
                                           size: "2px",
                                           style: "solid",
                                       }}
                                       round="xxsmall"
                                       align="center"
                                       justify="center"
                                       background={button.color}
                                       style={{ width: "30px", height: "30px" }}
                                   >
                                       {button.content}
                                   </Box>
                               </Box>
                               {isActive && (
                                   <Box>
                                       <Text
                                           size="medium"
                                           weight="bold"
                                           style={{
                                               marginLeft: "10px",
                                           }}
                                       >
                                           {button.label}
                                       </Text>
                                   </Box>
                               )}
                           </Box>

                           {/* Submenu Buttons */}
                           {isActive && button.subMenu && (
                               <Box
                                   background="light-2"
                                   pad="xxsmall"
                                   style={{
                                       position: "absolute",
                                       top: "50px",
                                       left: "120px",
                                       zIndex: 100,
                                       borderRadius: "4px",
                                       boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                                       minWidth: "260px",
                                       width: "auto",
                                       display: "block",
                                   }}
                                   onMouseEnter={handleSubmenuEnter}
                                   onMouseLeave={handleSubmenuLeave}
                               >
                                   {button.subMenu.map((subButton, index) => (
                                       <Box
                                           key={subButton.id}
                                           direction="row"
                                           align="center"
                                           justify="start"
                                           style={{
                                               marginTop: index === 0 ? "0px" : "5px",
                                               width: "100%",
                                               backgroundColor: subButton.color || "light-3",
                                               color: "white",
                                               borderRadius: "4px",
                                               cursor: "pointer",
                                               whiteSpace: "nowrap",
                                               padding: "5px 10px",
                                           }}
                                           onClick={subButton.onClick}
                                       >
                                           <Box
                                               border={{
                                                   color: subButton.color || "light-3",
                                                   size: "small",
                                                   style: "solid",
                                               }}
                                               round="small"
                                               align="center"
                                               justify="center"
                                               style={{ width: "35px", height: "35px" }}
                                           >
                                               <Box
                                                   border={{
                                                       color: "white",
                                                       size: "2px",
                                                       style: "solid",
                                                   }}
                                                   round="xxsmall"
                                                   align="center"
                                                   justify="center"
                                                   background={subButton.color || "light-3"}
                                                   style={{ width: "30px", height: "30px" }}
                                               >
                                                   {subButton.icon ? (
                                                       subButton.icon
                                                   ) : (
                                                       <Text size="large" weight="bold" color="white">
                                                           {subButton.letter}
                                                       </Text>
                                                   )}
                                               </Box>
                                           </Box>
                                           <Text
                                               size="medium"
                                               weight="bold"
                                               style={{
                                                   marginLeft: "10px",
                                               }}
                                           >
                                               {subButton.label}
                                           </Text>
                                       </Box>
                                   ))}
                               </Box>
                           )}
                       </Box>
                   );
               })}
       </Box>
   );
};

export default ExpandingButtonsMenu;





